/**
 * @fileoverview WB32COSIGN relay client + Orchard FROST initiator ceremony.
 *
 * A framework-agnostic port of the initiator half of WINBIT32's
 * `cosignerRelay`, scoped to the `frost-orchard` protocol (Secresea is
 * Orchard-only). Wire format, QR payload, AES-256-GCM envelopes and the
 * two-round message protocol are byte-compatible with WINBIT32, so the
 * existing `cosign.exe` cosigner interoperates unchanged.
 *
 * Reuse-oriented: the WASM engine is injected (no module-global), encryption
 * uses standard Web Crypto, and message delivery goes through any
 * {@link CosignTransport}. The DKLS / Schnorr / Sapling protocols and the
 * trusted-dealer DKG regen path are intentionally omitted — Secresea
 * co-signs an *existing* Orchard vault share, never creates one.
 */

import { hexToBytes, bytesToHex, toBase64, fromBase64 } from './bytes';
import type { CosignTransport } from './transports';
import {
	orchardCommitNonces,
	orchardSignWithNonces,
	orchardAggregate,
	type OrchardFrostWasmModule,
} from './orchardFrost';

const QR_PREFIX = 'WB32COSIGN';
const PROTOCOL_VERSION = 1;
const ROLE_INIT = 'init';
const ROLE_COSIGN = 'cosign';

const SESSION_TIMEOUT_MS = 180_000;
const POLL_IDLE_MS = 1_500;
const POLL_ACTIVE_MS = 500;

const MSG_TAG_JSON = 0x00;
const MSG_TAG_MPC_BINARY = 0x01;

// ── Public types ─────────────────────────────────────────────────────

export interface SigningDisplay {
	chain: string;
	amount?: string;
	toAddress?: string;
	ticker?: string;
	memo?: string;
}

export interface CosignSessionParams {
	sessionId: string;
	encKeyHex: string;
	/** Relay URL embedded in the QR (informational; transport may override). */
	relayUrl: string;
}

export interface FrostOrchardSetup {
	/** Per-action Orchard sighash (hex). */
	sighash: string;
	/** Per-action `alpha` randomiser the PCZT pre-committed to (64 hex chars). */
	randomizer: string;
	pubKeyPackage: { signerPubkeys: Record<string, string>; groupPublic: string };
}

export interface OrchardKeyShareForCosign {
	id: string;
	secretShare: string;
	publicKey: string;
	groupPublic: string;
}

export interface CosignCallbacks {
	onProgress?: (phase: string) => void;
	onCosignerJoined?: (partyId: string) => void;
	signal?: AbortSignal;
}

export interface MPCSignatureResult {
	/** 64-byte RedPallas signature (hex). */
	signature: string;
	rawSignatureBytes: Uint8Array;
}

/** Thrown when the cosigner asks to (re)derive keys — Secresea won't do that. */
export class OrchardShareMismatchError extends Error {
	readonly code = 'ORCHARD_SHARE_MISMATCH';
	constructor() {
		super(
			'The cosigner does not hold a matching Orchard FROST share for this vault. ' +
			'Load the correct .wult share on both devices — Secresea will not generate new keys.',
		);
	}
}

// ── Control messages (frost-orchard subset) ─────────────────────────

interface JoinMsg { type: 'join'; partyId: string; keyId: string }
interface SetupMsg {
	type: 'setup';
	protocol: 'frost-orchard';
	setupBytes: string;
	display: SigningDisplay;
	initiatorPartyId: string;
	setupId?: string;
	frostOrchardData?: FrostOrchardSetup;
}
interface FrostRound1Msg { type: 'frost-round1'; id: string; commitments: string; setupId?: string }
interface FrostRound2Msg { type: 'frost-round2'; id: string; sigShare: string; setupId?: string }
interface DoneMsg { type: 'done' }
interface ContinueMsg { type: 'continue' }
interface ErrorMsg { type: 'error'; message: string }
interface NeedDkgMsg { type: 'need-orchard-dkg'; partyId?: string }
interface MpcMsg { type: 'mpc'; body: string }

type ControlMsg =
	| JoinMsg | SetupMsg | FrostRound1Msg | FrostRound2Msg
	| DoneMsg | ContinueMsg | ErrorMsg | NeedDkgMsg | MpcMsg;

// ── Web Crypto helpers ───────────────────────────────────────────────

const getCrypto = (): Crypto => {
	const c = (globalThis as { crypto?: Crypto }).crypto;
	if (!c?.subtle) throw new Error('Web Crypto (crypto.subtle) is required for cosign relay encryption.');
	return c;
};

const importEncKey = (hexKey: string): Promise<CryptoKey> =>
	getCrypto().subtle.importKey('raw', hexToBytes(hexKey), 'AES-GCM', false, ['encrypt', 'decrypt']);

const encryptEnvelopeBytes = async (key: CryptoKey, plainBytes: Uint8Array): Promise<Uint8Array> => {
	const iv = getCrypto().getRandomValues(new Uint8Array(12));
	const cipherBuf = await getCrypto().subtle.encrypt({ name: 'AES-GCM', iv }, key, plainBytes);
	const combined = new Uint8Array(iv.length + cipherBuf.byteLength);
	combined.set(iv);
	combined.set(new Uint8Array(cipherBuf), iv.length);
	return combined;
};

const decryptEnvelopeBytes = async (key: CryptoKey, combined: Uint8Array): Promise<Uint8Array> => {
	const iv = combined.slice(0, 12);
	const ciphertext = combined.slice(12);
	const plainBuf = await getCrypto().subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
	return new Uint8Array(plainBuf);
};

/** v1 (legacy) string envelope: base64( iv || AES-GCM(utf8) ). */
const decryptEnvelopeString = async (key: CryptoKey, cipherB64: string): Promise<string> => {
	const combined = fromBase64(cipherB64);
	const plain = await decryptEnvelopeBytes(key, combined);
	return new TextDecoder().decode(plain);
};

/**
 * Push bytes through a (De)CompressionStream and collect the output. Uses the
 * stream's reader/writer directly rather than `Blob.stream()` so it works in
 * any host with Web Streams (browser, Node, jsdom) — not just DOM environments.
 */
const streamThrough = async (input: Uint8Array, transform: TransformStream<Uint8Array, Uint8Array>): Promise<Uint8Array> => {
	const writer = transform.writable.getWriter();
	void writer.write(input).then(() => writer.close()).catch(() => { /* surfaced via the reader */ });
	const reader = transform.readable.getReader();
	const chunks: Uint8Array[] = [];
	let total = 0;
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
		total += value.length;
	}
	const out = new Uint8Array(total);
	let offset = 0;
	for (const c of chunks) { out.set(c, offset); offset += c.length; }
	return out;
};

const deflateBytes = (input: Uint8Array): Promise<Uint8Array> =>
	streamThrough(input, new CompressionStream('deflate-raw'));

const inflateBytes = (input: Uint8Array): Promise<Uint8Array> =>
	streamThrough(input, new DecompressionStream('deflate-raw'));

const encodeControlMsg = (msg: ControlMsg): Uint8Array => {
	if (msg.type === 'mpc') {
		const body = fromBase64(msg.body);
		const out = new Uint8Array(1 + body.length);
		out[0] = MSG_TAG_MPC_BINARY;
		out.set(body, 1);
		return out;
	}
	const json = new TextEncoder().encode(JSON.stringify(msg));
	const out = new Uint8Array(1 + json.length);
	out[0] = MSG_TAG_JSON;
	out.set(json, 1);
	return out;
};

const decodeControlMsg = (bytes: Uint8Array): ControlMsg => {
	if (bytes[0] === MSG_TAG_MPC_BINARY) return { type: 'mpc', body: toBase64(bytes.slice(1)) };
	return JSON.parse(new TextDecoder().decode(bytes.slice(1)));
};

const transportSend = async (
	transport: CosignTransport,
	fromRole: string,
	toRole: string,
	key: CryptoKey,
	msg: ControlMsg,
): Promise<void> => {
	const raw = encodeControlMsg(msg);
	const compressed = await deflateBytes(raw);
	const encrypted = await encryptEnvelopeBytes(key, compressed);
	await transport.send(fromRole, toRole, toBase64(encrypted));
};

const transportReceive = async (
	transport: CosignTransport,
	myRole: string,
	key: CryptoKey,
): Promise<ControlMsg[]> => {
	const messages = await transport.receive(myRole);
	const results: ControlMsg[] = [];
	for (const encoded of messages) {
		try {
			const combined = fromBase64(encoded);
			const decrypted = await decryptEnvelopeBytes(key, combined);
			const decompressed = await inflateBytes(decrypted);
			results.push(decodeControlMsg(decompressed));
		} catch (v2Err) {
			try {
				results.push(JSON.parse(await decryptEnvelopeString(key, encoded)));
			} catch (v1Err) {
				console.warn(
					`[cosign/relay] dropped undecodable message for role=${myRole}`,
					'v2:', (v2Err as Error)?.message, 'v1:', (v1Err as Error)?.message,
				);
			}
		}
	}
	return results;
};

const checkAborted = (signal?: AbortSignal): void => {
	if (signal?.aborted) throw new Error('Cosign session cancelled');
};

const generateSetupId = (): string => bytesToHex(getCrypto().getRandomValues(new Uint8Array(8)));

// ── QR payload + session ─────────────────────────────────────────────

export const generateCosignQR = (params: CosignSessionParams): string => {
	const r = params.relayUrl ? `:${params.relayUrl}` : '';
	return `${QR_PREFIX}:${PROTOCOL_VERSION}:${params.sessionId}:${params.encKeyHex}${r}`;
};

export const parseCosignQR = (qr: string): CosignSessionParams | null => {
	if (!qr.startsWith(QR_PREFIX + ':')) return null;
	const parts = qr.split(':');
	if (parts.length < 4) return null;
	if (parseInt(parts[1], 10) !== PROTOCOL_VERSION) return null;
	return {
		sessionId: parts[2],
		encKeyHex: parts[3],
		relayUrl: parts.length >= 5 ? parts.slice(4).join(':') : '',
	};
};

/** Create a fresh session (random id + AES-256 key) and its QR payload. */
export const createCosignSession = (relayUrl?: string): { params: CosignSessionParams; qrPayload: string } => {
	const sessionId = getCrypto().randomUUID();
	const encKeyHex = bytesToHex(getCrypto().getRandomValues(new Uint8Array(32)));
	const params: CosignSessionParams = { sessionId, encKeyHex, relayUrl: relayUrl || '' };
	return { params, qrPayload: generateCosignQR(params) };
};

/** Tell the cosigner the session is finished so it stops polling. */
export const endCosignSession = async (encKeyHex: string, transport: CosignTransport): Promise<void> => {
	try {
		const key = await importEncKey(encKeyHex);
		await transportSend(transport, ROLE_INIT, ROLE_COSIGN, key, { type: 'done' });
	} catch (err) {
		console.warn('[cosign/relay] endCosignSession failed (non-fatal):', (err as Error)?.message);
	}
};

// ── Initiator: wait for cosigner to join ─────────────────────────────

export interface WaitForCosignerParams {
	encKeyHex: string;
	transport: CosignTransport;
	/** Vultisig localPartyIds permitted to join this vault's session. */
	validSignerIds: string[];
	callbacks?: CosignCallbacks;
}

/** Block until a valid cosigner scans the QR and joins; returns its partyId. */
export const waitForCosignerJoin = async (p: WaitForCosignerParams): Promise<string> => {
	const key = await importEncKey(p.encKeyHex);
	const start = Date.now();
	p.callbacks?.onProgress?.('Waiting for cosigner to scan QR...');
	for (;;) {
		checkAborted(p.callbacks?.signal);
		if (Date.now() - start > SESSION_TIMEOUT_MS) throw new Error('Timed out waiting for cosigner');
		const msgs = await transportReceive(p.transport, ROLE_INIT, key);
		for (const msg of msgs) {
			if (msg.type !== 'join') continue;
			if (!p.validSignerIds.includes(msg.partyId)) {
				await transportSend(p.transport, ROLE_INIT, ROLE_COSIGN, key, {
					type: 'error',
					message: 'Party ID not recognised for this vault',
				});
				continue;
			}
			p.callbacks?.onCosignerJoined?.(msg.partyId);
			return msg.partyId;
		}
		await new Promise((r) => setTimeout(r, POLL_IDLE_MS));
	}
};

// ── Initiator: one Orchard FROST action (two-round ceremony) ─────────

export interface RunOrchardInitiatorParams {
	/** Loaded Orchard FROST WASM engine. */
	wasm: OrchardFrostWasmModule;
	encKeyHex: string;
	transport: CosignTransport;
	myPartyId: string;
	orchardKeyShare: OrchardKeyShareForCosign;
	frostOrchardData: FrostOrchardSetup;
	display: SigningDisplay;
	callbacks?: CosignCallbacks;
	/** Keep the cosigner connected for a subsequent action (multi-action PCZTs). */
	keepAlive?: boolean;
}

/**
 * Run the two-round FROST-Orchard ceremony for a single Orchard action and
 * return its aggregated 64-byte signature. Mirrors WINBIT32's
 * `runInitiatorFrostOrchard` (minus the DKG regen branch).
 */
export const runOrchardInitiatorAction = async (p: RunOrchardInitiatorParams): Promise<MPCSignatureResult> => {
	const { wasm, transport, orchardKeyShare, frostOrchardData, display, callbacks } = p;
	const key = await importEncKey(p.encKeyHex);

	if (!/^[0-9a-fA-F]{64}$/.test(frostOrchardData.randomizer)) {
		throw new Error('Orchard FROST: missing/invalid per-action alpha randomiser (expected 64 hex chars).');
	}

	const setupId = generateSetupId();

	callbacks?.onProgress?.('Sending Orchard FROST setup to cosigner...');
	await transportSend(transport, ROLE_INIT, ROLE_COSIGN, key, {
		type: 'setup',
		protocol: 'frost-orchard',
		setupBytes: '',
		display,
		initiatorPartyId: p.myPartyId,
		setupId,
		frostOrchardData,
	});

	// Buffer the *entire* received batch before scanning for a match, so a
	// round2 that arrives in the same poll as round1 is never dropped (the
	// upstream impl returned on first hit, discarding the rest of the batch).
	const buffered: ControlMsg[] = [];
	const scanBuffered = <T>(predicate: (msg: ControlMsg) => T | undefined): T | undefined => {
		for (let i = 0; i < buffered.length; i++) {
			const hit = predicate(buffered[i]);
			if (hit !== undefined) { buffered.splice(i, 1); return hit; }
		}
		return undefined;
	};
	const pollFor = async <T>(predicate: (msg: ControlMsg) => T | undefined, label: string): Promise<T> => {
		const early = scanBuffered(predicate);
		if (early !== undefined) return early;
		const t0 = Date.now();
		for (;;) {
			checkAborted(callbacks?.signal);
			if (Date.now() - t0 > SESSION_TIMEOUT_MS) throw new Error(`Timed out waiting for ${label}`);
			const msgs = await transportReceive(transport, ROLE_INIT, key);
			for (const msg of msgs) {
				if (msg.type === 'error') throw new Error(`Cosigner: ${(msg as ErrorMsg).message}`);
				buffered.push(msg);
			}
			const hit = scanBuffered(predicate);
			if (hit !== undefined) return hit;
			await new Promise((r) => setTimeout(r, POLL_ACTIVE_MS));
		}
	};

	// Round 1 — commit nonces, exchange commitments.
	callbacks?.onProgress?.('Orchard FROST Round 1: committing nonces...');
	const myCommit = orchardCommitNonces(wasm, orchardKeyShare.secretShare);
	const r1Msg: FrostRound1Msg = {
		type: 'frost-round1', id: orchardKeyShare.id, commitments: myCommit.commitments, setupId,
	};
	await transportSend(transport, ROLE_INIT, ROLE_COSIGN, key, r1Msg);
	const r1Resend = resendLoop(() => transportSend(transport, ROLE_INIT, ROLE_COSIGN, key, r1Msg), callbacks?.signal);

	let round1: { id: string; commitments: string };
	try {
		round1 = await pollFor((m) => {
			if (m.type === 'frost-round1') {
				const r1 = m as FrostRound1Msg;
				if (r1.setupId && r1.setupId !== setupId) return undefined;
				return { id: r1.id, commitments: r1.commitments };
			}
			if (m.type === 'need-orchard-dkg') throw new OrchardShareMismatchError();
			return undefined;
		}, 'cosigner nonce commitments');
	} finally {
		r1Resend.stop();
	}

	// Round 2 — sign with pre-committed nonces.
	callbacks?.onProgress?.('Orchard FROST Round 2: signing...');
	const allCommitments: Record<string, string> = {
		[orchardKeyShare.id]: myCommit.commitments,
		[round1.id]: round1.commitments,
	};
	const allCommitmentsJson = JSON.stringify(allCommitments);

	const mySigShare = orchardSignWithNonces(
		wasm,
		orchardKeyShare.id,
		orchardKeyShare.secretShare,
		orchardKeyShare.publicKey,
		frostOrchardData.pubKeyPackage.groupPublic,
		myCommit.noncesOpaque,
		allCommitmentsJson,
		frostOrchardData.sighash,
		frostOrchardData.randomizer,
	);

	const r2Msg: FrostRound2Msg = { type: 'frost-round2', id: orchardKeyShare.id, sigShare: mySigShare, setupId };
	await transportSend(transport, ROLE_INIT, ROLE_COSIGN, key, r2Msg);
	const r2Resend = resendLoop(() => transportSend(transport, ROLE_INIT, ROLE_COSIGN, key, r2Msg), callbacks?.signal);

	const mySignerId = orchardKeyShare.id;
	let round2: { sigShare: string };
	try {
		round2 = await pollFor((m) => {
			if (m.type !== 'frost-round2') return undefined;
			const r2 = m as FrostRound2Msg;
			if (!r2.id || r2.id === mySignerId) return undefined;
			if (r2.setupId && r2.setupId !== setupId) return undefined;
			return { sigShare: r2.sigShare };
		}, 'cosigner signature share');
	} finally {
		r2Resend.stop();
	}

	// Aggregate.
	callbacks?.onProgress?.('Aggregating Orchard FROST signature...');
	const sigShares: Record<string, string> = {
		[orchardKeyShare.id]: mySigShare,
		[round1.id]: round2.sigShare,
	};
	const aggregate = JSON.parse(orchardAggregate(
		wasm,
		allCommitmentsJson,
		frostOrchardData.sighash,
		JSON.stringify(sigShares),
		JSON.stringify(frostOrchardData.pubKeyPackage.signerPubkeys),
		frostOrchardData.pubKeyPackage.groupPublic,
		frostOrchardData.randomizer,
	));
	const signature: string = aggregate.signature;

	callbacks?.onProgress?.('Orchard FROST signature complete');
	await transportSend(transport, ROLE_INIT, ROLE_COSIGN, key,
		p.keepAlive ? { type: 'continue' } : { type: 'done' });

	return { signature, rawSignatureBytes: hexToBytes(signature) };
};

/**
 * Periodically re-send a message (the relay/cosigner may miss the first
 * delivery). Stops automatically once `.stop()` is called or the signal aborts.
 */
const resendLoop = (
	send: () => Promise<void>,
	signal?: AbortSignal,
): { stop: () => void } => {
	const state = { stopped: false };
	const DELAYS_MS = [2_000, 4_000, 8_000, 15_000, 25_000, 40_000, 60_000];
	void (async () => {
		for (const delay of DELAYS_MS) {
			await new Promise((r) => setTimeout(r, delay));
			if (state.stopped || signal?.aborted) return;
			try {
				await send();
			} catch (e) {
				console.warn('[cosign/relay] resend failed:', (e as Error)?.message);
			}
		}
	})();
	return { stop: () => { state.stopped = true; } };
};
